import { OrganizationService } from '@/modules/databaseService/types';
import { updateMyOrgMember, renameOrg } from '@/infrastructure/clients/orgsApi';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import { orgMemberToLegacy } from '@/infrastructure/api/v1/mappers/orgMembers';
import { orgToLegacy } from '@/infrastructure/api/v1/mappers/orgs';
import { convertToOrgMember, convertToOrgInstance } from '@/utils/dataConverters';
import { SupabaseOrgMember, OrgMember, UpdateOrgMemberData, SupabaseOrgInstance, OrgInstance } from '@/types/orgTypes';
import {
  getMemberPreferences,
  invalidateMemberPreferences,
  patchMemberPreferences,
  changedPreferences,
} from './memberPreferencesCache';

export class OrganizationAdapter implements OrganizationService {
  async fetchMemberPreferences(userId: string): Promise<Record<string, any>> {
    return getMemberPreferences(userId);
  }

  // Callers pass the blob they read plus their change: only the keys that differ from that read go onto a fresh one
  async updateMemberPreferences(userId: string, preferences: Record<string, any>): Promise<void> {
    const patch = changedPreferences(userId, preferences);
    if (Object.keys(patch).length === 0) return;
    try {
      await patchMemberPreferences(userId, patch);
    } catch (error) {
      console.error('Error updating member preferences:', error);
      throw error;
    }
  }

  // The membership is always the signed-in user's; null only when they are in no org, a failed read throws
  async fetchOrgMember(_userId: string): Promise<OrgMember | null> {
    const { data, error } = await toResult(v1.GET('/api/v1/orgs/me/member'));
    if (error) {
      if (error.status === 404) return null;
      console.error('Error fetching org member:', error);
      throw error;
    }
    return convertToOrgMember(orgMemberToLegacy(data) as unknown as SupabaseOrgMember);
  }

  async updateOrgMember(userId: string, updateData: UpdateOrgMemberData): Promise<void> {
    try {
      await updateMyOrgMember(updateData);
      // If this update touched member_preferences, drop the cache so the next
      // read sees the merged server value (we don't have the full new blob here).
      if ('member_preferences' in updateData) {
        invalidateMemberPreferences(userId);
      }
    } catch (error) {
      console.error('Error updating org member:', error);
      throw error;
    }
  }

  // Null only when there is no such org; a read that failed throws its problem
  async fetchOrgInstance(orgInstanceId: string): Promise<OrgInstance | null> {
    const { data, error } = await toResult(v1.GET('/api/v1/orgs/{orgInstanceUniqueId}', { params: { path: { orgInstanceUniqueId: orgInstanceId } } }));
    if (error) {
      if (error.status === 404) return null;
      console.error('Error fetching org instance:', error);
      throw error;
    }
    return convertToOrgInstance(orgToLegacy(data) as unknown as SupabaseOrgInstance);
  }

  // Null when the user is in no org; a read that failed throws its problem
  async fetchMyOrgInstance(): Promise<OrgInstance | null> {
    const { data, error } = await toResult(v1.GET('/api/v1/orgs/me'));
    if (error) {
      if (error.status === 404) return null;
      console.error('Error fetching org instance:', error);
      throw error;
    }
    return convertToOrgInstance(orgToLegacy(data) as unknown as SupabaseOrgInstance);
  }

  // Null when the user is in no org; a read that failed throws its problem
  async fetchOrgInstanceByUserId(userId: string): Promise<OrgInstance | null> {
    const member = await this.fetchOrgMember(userId);
    return member ? this.fetchOrgInstance(member.orgInstanceId) : null;
  }

  async updateOrgInstanceDisplayName(userId: string, displayName: string): Promise<void> {
    try {
      // First get the user's org_instance_id from org_members
      const member = await this.fetchOrgMember(userId);
      if (!member) {
        throw new Error('User not found in organization');
      }

      // Update the org instance display name
      await renameOrg(member.orgInstanceId, displayName);
    } catch (error) {
      console.error('Error updating org instance display name:', error);
      throw error;
    }
  }

  async updateOrgInstanceImmersalToken(userId: string, token: string): Promise<void> {
    try {
      const member = await this.fetchOrgMember(userId);
      if (!member) {
        throw new Error('User not found in organization');
      }

      // Only this operation writes the token; the backend checks the bearer belongs to a member
      // of the org. An empty token clears it.
      const { error } = await toResult(v1.PUT('/api/v1/orgs/{orgInstanceUniqueId}/immersal-token', {
        params: { path: { orgInstanceUniqueId: member.orgInstanceId } },
        body: { token },
      }));

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error updating org instance Immersal token:', error);
      throw error;
    }
  }
}

